import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api/client";
import { useParams2 } from "../contexts/ParamsContext";

const PUB = process.env.PUBLIC_URL || "";

const TARIFS = {
  particulier_commune: { label: "Particulier - Commune", weekend: 300, journee: 150 },
  particulier_hors:    { label: "Particulier - Hors commune", weekend: 400, journee: 200 },
  association_commune: { label: "Association - Commune", weekend: 70,  journee: 35 },
  association_hors:    { label: "Association - Hors commune", weekend: 100, journee: 50 },
};

function fmtFR(iso) {
  if (!iso) return "_____________";
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR",
    { day: "2-digit", month: "2-digit", year: "numeric" });
}

function daysBetween(d1, d2) {
  if (!d1 || !d2) return 1;
  return Math.floor((new Date(d2 + "T00:00:00") - new Date(d1 + "T00:00:00")) / 86400000) + 1;
}

export default function ContratSalle() {
  const { id } = useParams();
  const params = useParams2();
  const [resa, setResa] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/salle/reservations/${id}`)
      .then(setResa)
      .catch(() => setError("Réservation introuvable. Vérifiez le lien."));
  }, [id]);

  if (error) {
    return <div className="container"><div className="error">{error}</div></div>;
  }
  if (!resa) {
    return <div className="container"><p className="muted">Chargement du contrat…</p></div>;
  }

  const nbJours = daysBetween(resa.date_debut, resa.date_fin);
  const tarifInfo = TARIFS[resa.type_tarif] || TARIFS.particulier_hors;
  const tarifCalcule = nbJours >= 2 ? tarifInfo.weekend : tarifInfo.journee;

  return (
    <div className="container">
      <div className="contrat-toolbar no-print">
        <Link to={`/reserver-salle`} className="secondary"
              style={{ padding: "0.5rem 1rem", border: "1px solid rgba(0,0,0,0.15)",
                       borderRadius: 6, textDecoration: "none" }}>
          ← Retour
        </Link>
        <h2 style={{ margin: 0 }}>Contrat de location – Pré-rempli</h2>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Link to={`/etat-des-lieux/${id}`}
                style={{ padding: "0.7rem 1.2rem", borderRadius: 6,
                         background: "#fff", color: "var(--accent)",
                         border: "1.5px solid var(--accent)",
                         textDecoration: "none", fontWeight: 500 }}>
            📋 État des lieux
          </Link>
          <button onClick={() => window.print()}>
            🖨 Imprimer / Sauvegarder en PDF
          </button>
        </div>
      </div>

      <div className="contrat-notice no-print">
        <strong>📋 Procédure</strong>
        <ol style={{ marginBottom: 0 }}>
          <li>Vérifiez les informations préremplies ci-dessous</li>
          <li>Imprimez le contrat (ou sauvegardez-le en PDF via Ctrl+P → "Enregistrer au format PDF")</li>
          <li>Complétez à la main les champs manquants (adresse complète, signature)</li>
          <li>Joignez votre attestation d'assurance responsabilité civile</li>
          <li>Remettez l'ensemble à la mairie aux heures d'ouverture</li>
        </ol>
      </div>

      {/* ============== CONTRAT IMPRIMABLE ============== */}
      <article className="contrat-doc">
        {/* En-tête mairie */}
        <header className="contrat-header">
          <img src={`${PUB}/logo-lcsf.png`} alt="Blason" />
          <div>
            <strong>Département de la Sarthe</strong><br/>
            <strong>{params.mairie_nom || "Mairie de La Chapelle-Saint-Fray"}</strong><br/>
            {params.mairie_adresse || "Route du Mans"}<br/>
            {(params.mairie_cp || "72240") + " " + (params.mairie_ville || "La Chapelle-Saint-Fray")}<br/>
            Tél : {params.mairie_telephone || "02 43 20 90 38"}<br/>
            Mail : {params.mairie_email || "secretariat@lachapellesaintfray.fr"}
          </div>
        </header>

        <h1 className="contrat-title">Contrat de location de la salle communale</h1>

        <p className="contrat-ref muted">
          Référence demande : <strong>n° {resa.id}</strong> – Reçue le {fmtFR(resa.created_at?.slice(0,10))}
        </p>

        {/* PARTIES */}
        <section>
          <p><strong>Entre :</strong></p>
          <p className="contrat-block">
            {params.mairie_nom || "Mairie de La Chapelle-Saint-Fray"},<br/>
            représentée par M. / Mme le Maire en exercice<span className="fillable"> (ou son adjoint délégué : ………………………………………………………………,
            en vertu de la délibération de délégation n° ………… du …………/…………/…………)</span>,<br/>
            {params.mairie_adresse || "Route du Mans"} – {(params.mairie_cp || "72240") + " " + (params.mairie_ville || "La Chapelle-Saint-Fray")}<br/>
            Ci-après dénommée « le bailleur »,
          </p>

          <p><strong>Et :</strong></p>
          <table className="contrat-table">
            <tbody>
              <tr><td>Nom, prénom :</td><td><strong>{resa.nom?.toUpperCase()} {resa.prenom}</strong></td></tr>
              <tr><td>Date et lieu de naissance :</td><td className="fillable">..…./..…./..………… à ………………………………………</td></tr>
              <tr><td>Adresse :</td>
                  <td><strong>{resa.adresse || "…………………………………………………………………………………………"}</strong></td></tr>
              {!resa.adresse && (<tr><td></td><td className="fillable">………………………………………………………………………………………………</td></tr>)}
              <tr><td>Commune :</td><td><strong>{resa.commune || "………………………………………"}</strong></td></tr>
              <tr><td>Email :</td><td><strong>{resa.email}</strong></td></tr>
              <tr><td>Téléphone :</td><td><strong>{resa.telephone || "…………………………………………"}</strong></td></tr>
              <tr><td>Pièce d'identité :</td><td className="fillable">Type ……………………… N° …………………………………………………</td></tr>
            </tbody>
          </table>
          <p>
            Le locataire, signataire du présent contrat, certifie être <strong>majeur</strong>
            (18 ans révolus) et seul responsable des obligations qui en découlent.
            Ci-après dénommé(e) « le locataire ».
          </p>
        </section>

        {/* ARTICLE 1 - LOCAUX */}
        <section>
          <h3>Article 1 — Désignation des locaux et capacité</h3>
          <p>Les locaux concernés par la présente location comprennent :</p>
          <ul>
            <li>La salle communale, refaite à neuf en 2024</li>
            <li>La cuisine équipée</li>
            <li>Les sanitaires (dont un accessible PMR)</li>
          </ul>
          <p>
            <strong>Capacité maximale d'accueil : 60 personnes assises.</strong>{" "}
            La salle est un établissement recevant du public (ERP) ; le locataire s'engage à
            respecter strictement cette capacité, à ne pas obstruer les issues de secours, et à
            préserver l'accès aux équipements de sécurité incendie.
          </p>
        </section>

        {/* ARTICLE 2 - ÉQUIPEMENTS */}
        <section>
          <h3>Article 2 — Équipements mis à disposition</h3>
          <ul>
            <li>Tables et chaises (en nombre adapté aux invités)</li>
            <li>Réfrigérateur(s), congélateur</li>
            <li>Cuisinière 5 feux, grand four</li>
            <li>Lave-vaisselle, évier</li>
            <li>Vidéoprojecteur, barre de son, Internet, Wi-Fi, Bluetooth</li>
            <li>Fils en acier latéraux pour décorations</li>
            <li>Matériel d'entretien : aspirateur, seaux, serpillières</li>
          </ul>
          <p>
            La vaisselle et le linge de table ne sont pas fournis. Le matériel est remis propre
            et en bon état de fonctionnement ; il doit être restitué dans le même état. Tables
            et chaises devront être remis à leur emplacement initial.
            Un état des lieux contradictoire (entrée et sortie) sera dressé entre les parties.
          </p>
        </section>

        {/* ARTICLE 3 - DESTINATION */}
        <section>
          <h3>Article 3 — Destination des lieux</h3>
          <p>
            La salle est louée pour l'événement suivant :{" "}
            <strong>{resa.type_evenement || "………………………………………………………………………………………………"}</strong>
            {resa.nb_personnes > 0 && <>, pour environ <strong>{resa.nb_personnes} personnes</strong></>}.
          </p>
          <p>
            <strong>Sont expressément interdits :</strong> les soirées dansantes avec billetterie
            ou ouvertes au public sans autorisation préalable de la préfecture, l'organisation
            de débits de boissons sans licence, toute activité à caractère politique
            (sauf période électorale, dans le respect de la réglementation et après accord
            écrit du maire), toute activité cultuelle, toute activité commerciale lucrative
            non déclarée. La location à un mineur n'est pas autorisée : seul un majeur peut
            signer le présent contrat et assume la pleine responsabilité de l'usage des locaux.
          </p>
          <p>
            <strong>Buvette :</strong> l'ouverture d'une buvette temporaire est soumise à
            l'autorisation préalable du maire, sur demande écrite déposée en mairie au moins
            15 jours avant la manifestation, conformément à la réglementation en vigueur sur
            les débits de boissons.
          </p>
          <p>
            <strong>Diffusion musicale (SACEM) :</strong> en cas de diffusion de musique
            (enregistrée ou live), le locataire fait son affaire personnelle des déclarations
            et redevances dues à la SACEM ou tout autre organisme de gestion collective.
            La mairie ne saurait être tenue pour responsable d'un défaut de déclaration.
          </p>
          <p>
            <strong>Respect du voisinage :</strong> conformément à l'arrêté municipal,
            la musique devra être modérée à partir de <strong>22 h</strong> et cesser à
            <strong> 1 h</strong> du matin. Le niveau acoustique de la sonorisation ne devra
            pas dépasser <strong>105 dB</strong>. Les portes et fenêtres extérieures seront
            maintenues fermées en cas de diffusion sonore.
          </p>
        </section>

        {/* ARTICLE 4 - DURÉE ET HORAIRES */}
        <section>
          <h3>Article 4 — Durée de la location et états des lieux</h3>
          <p>
            La location débute le <strong>{fmtFR(resa.date_debut)}</strong> à{" "}
            <strong>{resa.heure_debut || "14:00"}</strong>{" "}
            et prend fin le <strong>{fmtFR(resa.date_fin)}</strong> à{" "}
            <strong>{resa.heure_fin || "18:00"}</strong>.
          </p>
          <p>
            La remise des clés s'effectue lors de l'<strong>état des lieux d'entrée</strong>,
            contradictoire entre les parties et signé. <strong>Toute réserve ou anomalie
            constatée doit impérativement être signalée lors de cet état des lieux d'entrée</strong> :
            à défaut, l'état des lieux fait foi et le locataire en sera tenu responsable
            à la sortie.
          </p>
          <p>
            Le transfert de responsabilité s'opère dès la signature de l'état des lieux d'entrée
            jusqu'à la signature de celui de sortie. La salle doit être vidée, nettoyée et rendue
            dans son état initial à l'heure de fin convenue. L'état des lieux de sortie et la
            restitution des clés constatent contradictoirement la libération des lieux.
          </p>
        </section>

        {/* ARTICLE 5 - GARANTIES ET ASSURANCE */}
        <section>
          <h3>Article 5 — Dépôts de garantie et obligations préalables</h3>
          <p>À la signature du contrat, le locataire remet :</p>
          <ul>
            <li>
              Un <strong>chèque de dépôt de garantie de 1 500,00&nbsp;€</strong> pour la
              salle (dégâts éventuels, matériel manquant), à l'ordre du Trésor Public —
              non encaissé sauf en cas de sinistre constaté contradictoirement ;
            </li>
            <li>
              Un <strong>chèque de dépôt de garantie de 400,00&nbsp;€</strong> pour le
              ménage, à l'ordre du Trésor Public — non encaissé sauf si le nettoyage est
              jugé insuffisant à l'état des lieux de sortie ;
            </li>
            <li>
              Une <strong>attestation d'assurance de responsabilité civile</strong> couvrant
              spécifiquement la période et l'événement loué, avec mention de la salle communale,
              garantissant au minimum <strong>1 000 000&nbsp;€</strong> en dommages corporels ;
            </li>
            <li>Le présent contrat daté et signé, ainsi que le règlement intérieur (annexé).</li>
          </ul>
          <p>
            Ces deux chèques sont des <strong>dépôts de garantie</strong> et non des arrhes :
            ils ne sont pas encaissés sauf cas justifié. Ils seront restitués dans un délai
            maximum de <strong>30 jours</strong> après l'état des lieux de sortie, sous réserve
            qu'aucun sinistre ou nettoyage complémentaire n'ait été constaté. En cas de dégâts
            excédant le montant des garanties, le locataire reste redevable du complément, et
            la mairie se réserve le droit d'engager toute action utile pour le recouvrement.
          </p>
        </section>

        {/* ARTICLE 6 - TARIF */}
        <section>
          <h3>Article 6 — Tarif et règlement</h3>
          <p>
            Type de tarification : <strong>{tarifInfo.label}</strong><br/>
            Tarif applicable : <strong>{tarifCalcule}&nbsp;€</strong> (électricité incluse).
          </p>
          <p className="muted" style={{ fontSize: "0.9em" }}>
            Tarifs fixés par délibération du Conseil municipal n°&nbsp;
            <span className="fillable">………… du …………/…………/…………</span>
          </p>
          <table className="contrat-table contrat-tarifs">
            <thead>
              <tr><th>Tarification week-end</th><th>Commune</th><th>Hors commune</th></tr>
            </thead>
            <tbody>
              <tr><td>Particulier</td><td>300 €</td><td>400 €</td></tr>
              <tr><td>Association</td><td>70 €</td><td>100 €</td></tr>
            </tbody>
          </table>
          <p>
            Mode de règlement accepté : chèque bancaire à l'ordre du <strong>Trésor Public</strong>,
            à remettre lors de la signature du contrat. Conformément à l'article 261 D du Code
            général des impôts, cette location de salle communale n'est pas soumise à la TVA.
          </p>
        </section>

        {/* ARTICLE 7 - CESSION */}
        <section>
          <h3>Article 7 — Cession et sous-location</h3>
          <p>
            Toute sous-location est interdite. Le présent titre de location est strictement
            nominatif et ne peut être cédé à un tiers, à quelque titre que ce soit. Le
            locataire signataire demeure seul responsable de l'usage de la salle pendant
            toute la durée de la location.
          </p>
        </section>

        {/* ARTICLE 8 - ANNULATION */}
        <section>
          <h3>Article 8 — Annulation</h3>
          <p>
            <strong>Annulation par le locataire :</strong> si l'annulation intervient plus de
            30 jours avant la date de location, aucun frais n'est retenu et les chèques de
            garantie sont restitués. Entre 30 et 8 jours avant la date, 50&nbsp;% du tarif sont
            conservés. À moins de 8 jours, le tarif est dû en totalité, sauf cas de force majeure
            dûment justifié.
          </p>
          <p>
            <strong>Annulation par la mairie :</strong> en cas d'impératif communal (élection,
            réquisition, sinistre, force majeure), la mairie pourra annuler la location ou
            la reporter. Les sommes éventuellement versées seront remboursées intégralement,
            mais cette annulation <strong>n'ouvre droit à aucune indemnité ni dédommagement
            supplémentaire</strong> pour le locataire.
          </p>
        </section>

        {/* ARTICLE 9 - RESPONSABILITÉ ET VOL */}
        <section>
          <h3>Article 9 — Responsabilité et limitation</h3>
          <p>
            La Commune ne saurait être tenue pour responsable des vols, pertes ou détériorations
            de marchandises, objets personnels, denrées ou tout article que le locataire ou ses
            invités introduiraient ou entreposeraient dans la salle ou ses abords, avant, pendant
            ou après la manifestation. Le locataire est seul responsable du gardiennage et de
            la sécurité de ses biens et de ceux de ses invités.
          </p>
          <p>
            Le locataire est responsable de toute dégradation causée aux locaux, équipements
            et abords de la salle pendant la durée de la location, qu'elle soit de son fait,
            de celui de ses invités ou de prestataires intervenant à sa demande.
          </p>
        </section>

        {/* ARTICLE 10 - RÈGLEMENT INTÉRIEUR */}
        <section>
          <h3>Article 10 — Règlement intérieur et obligations générales</h3>
          <p>
            Le locataire déclare avoir pris connaissance du règlement intérieur de la salle
            communale (annexé au présent contrat) et s'engage à le respecter et le faire
            respecter par ses invités. Il s'engage notamment à utiliser les locaux dans le
            respect des lois et réglementations en vigueur (hygiène, sécurité, ordre public,
            fiscalité, SACEM, débits de boissons).
          </p>
        </section>

        {/* ARTICLE 11 - LITIGE */}
        <section>
          <h3>Article 11 — Litige et tribunal compétent</h3>
          <p>
            En cas de différend, les parties s'engagent à rechercher une solution amiable
            avant toute action contentieuse. À défaut, le Tribunal administratif de Nantes
            est seul compétent.
          </p>
        </section>

        {/* SIGNATURES */}
        <section className="contrat-signatures">
          <p>
            Fait à La Chapelle-Saint-Fray, le ……..…/…………/…………<br/>
            En deux exemplaires originaux, dont un remis au locataire.
          </p>
          <div className="contrat-signature-row">
            <div className="contrat-signature-box">
              <strong>Pour le bailleur</strong><br/>
              Le Maire,<br/>
              <em>Mention « Lu et approuvé »</em>
            </div>
            <div className="contrat-signature-box">
              <strong>Le locataire</strong><br/>
              {resa.prenom} {resa.nom}<br/>
              <em>Mention « Lu et approuvé »</em>
            </div>
          </div>
        </section>

        <footer className="contrat-footer muted">
          Document généré automatiquement à partir de la demande n° {resa.id} – {params.mairie_nom || "Mairie de La Chapelle-Saint-Fray"}
        </footer>
      </article>
    </div>
  );
}
